import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Avatar,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/axios";

// Clear old cached messages that may contain wrong links (browse → / instead of /search)
try { localStorage.removeItem("chatbot_messages"); } catch {}

const STORAGE_KEY = "chatbot_messages";
const WELCOME_MESSAGE = {
  role: "assistant",
  content: "Hi! I'm your PepperDiamond assistant. I can help you with order tracking, account settings, addresses, wishlist, and more. How can I help you today?",
  id: "welcome",
};

// Simple markdown renderer: **bold**, [text](/link), newlines
function renderMessageContent(text) {
  if (!text) return "";
  let html = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#1565c0;text-decoration:underline;font-weight:600">$1</a>');
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

export default function CustomerChatBot() {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [WELCOME_MESSAGE];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [orders, setOrders] = useState([]);
  const [userContext, setUserContext] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const chatEndRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {}
  }, [messages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-chat", handler);
    return () => window.removeEventListener("open-chat", handler);
  }, []);

  useEffect(() => {
    if (!user?.username) return;

    const fetchData = async () => {
      try {
        const [ordersRes, wishlistRes, cartRes] = await Promise.all([
          api.get(`/orders/customer/${user.username}?page=1&limit=5`),
          api.get("/wishlist").catch(() => ({ data: { items: [] } })),
          api.get("/cart/count").catch(() => ({ data: { count: 0 } })),
        ]);

        const rawOrders = ordersRes.data.orders || [];
        const simplifiedOrders = rawOrders.map((o) => ({
          _id: o._id,
          total: o.total,
          status: o.status,
          tracking: (o.tracking || []).map(t => ({ key: t.key, label: t.label })),
          items: (o.items || []).map(i => ({ title: i.title, qty: i.qty, price: i.price })),
          address: o.address ? { addressLine1: o.address.addressLine1, city: o.address.city, state: o.address.state, pincode: o.address.pincode } : null,
          payment: o.payment ? { method: o.paymentMethod, status: o.payment.status } : null,
          createdAt: o.createdAt,
        }));
        setOrders(simplifiedOrders);

        const statusCounts = {};
        rawOrders.forEach(o => {
          const lastKey = o.tracking?.at(-1)?.key || o.status;
          statusCounts[lastKey] = (statusCounts[lastKey] || 0) + 1;
        });

        setUserContext({
          name: user.firstName || user.username || "Customer",
          email: user.email || null,
          role: user.role,
          hasAddresses: (user.addresses?.length || 0) > 0,
          addressCount: user.addresses?.length || 0,
          orderCount: rawOrders.length,
          orderStatusSummary: statusCounts,
          wishlistCount: (wishlistRes.data?.items || []).length,
          cartCount: cartRes.data?.count || 0,
        });
      } catch {}
    };

    fetchData();
  }, [user?.username]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    startTimeRef.current = Date.now();
    const userMsg = { role: "user", content: text, id: `user-${Date.now()}` };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);
    setResponseTime(null);

    try {
      const { data } = await api.post("/ai/customer-chat", {
        messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        userContext,
        orders,
      });

      const elapsed = Date.now() - startTimeRef.current;
      setResponseTime(elapsed);

      if (data.success) {
        setMessages(prev => [...prev, { role: "assistant", content: data.response, id: `ai-${Date.now()}` }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Please try again.", id: `ai-${Date.now()}` }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again later.", id: `ai-${Date.now()}` }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, userContext, orders]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFeedback = (messageId, type) => {
    setFeedbackGiven(prev => ({ ...prev, [messageId]: type }));
  };

  const handleResetChat = () => {
    setMessages([WELCOME_MESSAGE]);
    setFeedbackGiven({});
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const handleMessageClick = (e) => {
    const link = e.target.closest('a');
    if (link && link.getAttribute('href')) {
      const href = link.getAttribute('href');
      if (href.startsWith('/')) {
        e.preventDefault();
        window.location.href = href;
      }
    }
  };

  // Chatbot is enabled by default; set REACT_APP_CHATBOT_ENABLED=false in .env to disable
  const chatbotEnabled = process.env.REACT_APP_CHATBOT_ENABLED !== "false";

  if (!user || user.role !== "customer" || !user.profileCompleted || !chatbotEnabled) return null;

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setOpen(!open)}
        sx={{
          position: "fixed",
          bottom: { xs: 80, md: 24 },
          right: { xs: 16, md: 24 },
          zIndex: 1600,
          bgcolor: "rgba(0,0,0,0.04)",
          color: "#000",
          "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
          width: { xs: 38, md: 48 },
          height: { xs: 38, md: 48 },
          minHeight: 0,
          boxShadow: "none",
          borderRadius: "50%",
        }}
      >
        {open ? <CloseIcon /> : (
          <Box
            component="img"
            src="/customerSupport.png"
            alt="Support"
            sx={{ width: { xs: 22, md: 28 }, height: { xs: 22, md: 28 }, borderRadius: "50%" }}
          />
        )}
      </Fab>

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: { xs: 136, md: 96 },
            right: 24,
            width: { xs: "calc(100% - 48px)", sm: 400 },
            height: 560,
            maxHeight: "calc(100dvh - 140px)",
            zIndex: 1600,
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ p: 1.5, bgcolor: "#131921", color: "#fff", display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: "#fe9800" }}>
              <Box component="img" src="/customerSupport.png" alt="" sx={{ width: 20, height: 20 }} />
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Customer Support</Typography>
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
                {loading ? "Typing..." : responseTime ? `Responded in ${(responseTime / 1000).toFixed(1)}s` : "Online"}
              </Typography>
            </Box>
            <Tooltip title="Reset conversation">
              <IconButton size="small" onClick={handleResetChat} sx={{ color: "rgba(255,255,255,0.7)" }}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,0.7)" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 1.5, bgcolor: "#f5f5f5", display: "flex", flexDirection: "column", gap: 1 }} onClick={handleMessageClick}>
            {messages.map((msg, i) => (
              <Box key={msg.id || i}>
                <Box sx={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                  <Paper elevation={0} sx={{ p: 1.5, maxWidth: "85%", borderRadius: 2, bgcolor: msg.role === "user" ? "#fe9800" : "#fff", color: msg.role === "user" ? "#000" : "inherit", fontSize: 13, lineHeight: 1.5, borderBottomRightRadius: msg.role === "user" ? 4 : 2, borderBottomLeftRadius: msg.role === "assistant" ? 4 : 2, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    <Typography component="div" variant="body2" sx={{ fontSize: 13, lineHeight: 1.5, '& strong': { fontWeight: 700 }, '& a': { color: '#1565c0', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer' } }}
                      dangerouslySetInnerHTML={{ __html: msg.role === "assistant" ? renderMessageContent(msg.content) : msg.content }}
                    />
                  </Paper>
                </Box>
                {msg.role === "assistant" && msg.id && msg.id !== "welcome" && (
                  <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 0.5, mt: 0.25, ml: 0.5 }}>
                    <Tooltip title="Helpful">
                      <IconButton size="small" onClick={() => handleFeedback(msg.id, "up")} sx={{ color: feedbackGiven[msg.id] === "up" ? "#2e7d32" : "#999", width: 24, height: 24 }}>
                        <ThumbUpIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Not helpful">
                      <IconButton size="small" onClick={() => handleFeedback(msg.id, "down")} sx={{ color: feedbackGiven[msg.id] === "down" ? "#d32f2f" : "#999", width: 24, height: 24 }}>
                        <ThumbDownIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#fff" }}>
                  <CircularProgress size={16} sx={{ color: "#fe9800" }} />
                </Paper>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>

          <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "#fff", flexShrink: 0 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField fullWidth size="small" placeholder="Type your message..." value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} disabled={loading} multiline maxRows={3}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
              <IconButton onClick={handleSend} disabled={loading || !input.trim()}
                sx={{ bgcolor: "#fe9800", color: "#000", borderRadius: 2, width: 40, height: 40, "&:hover": { bgcolor: "#e68a00" }, "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#999" } }}>
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
    </>
  );
}